/**
 * Feeds `agent.working` frames into the agent-working store.
 *
 * Mounted once at AppShell scope (beside `useGlobalRealtime`) so the store
 * stays warm whichever surface is on screen — a card that starts glowing
 * while its board is closed is already correct when the board opens.
 *
 * A `stop` frame also refreshes the card's single-card cache: the run's
 * time and cost are written to the card when it settles, so an open card
 * modal's Time row is stale the moment the glow goes out.
 *
 * `agent.turn.finished` is a second, independent way out: an agent runs one
 * turn at a time, so its end clears every glow the agent holds and refreshes
 * those cards, covering a per-card stop frame that never arrived.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import {
  DEEP_LINK_CARD_QUERY_KEY,
  DEEP_LINK_CARD_QUERY_PREFIX,
} from '@/features/board/use-deep-link-card'
import { AGENTS_QUERY_KEY } from '@/features/tenant-settings/agents/use-agents'
import { workstationQueryKey } from '@/features/tenant-settings/agents/use-workstation'
import { noteWorkstationFrame } from '@/features/tenant-settings/agents/workstation-frames'
import type { ServerMessage } from '@/lib/use-websocket'
import { useWebSocketSubscription } from '@/lib/use-websocket-subscription'

import { type AgentWorkingFramePayload, agentWorkingStore } from './agent-working-store'

export const AGENT_WORKING_FRAME = 'agent.working'
export const AGENT_TURN_FINISHED_FRAME = 'agent.turn.finished'
export const AGENT_WORKSTATION_FRAME = 'agent.workstation.changed'

/** The agent and (when it has one) focus card of a usable `agent.workstation.changed` frame, else `null`. */
export function parseAgentWorkstationFrame(
  message: ServerMessage,
): { agentUserId: string; focusCardId: string | null } | null {
  if (message.type !== AGENT_WORKSTATION_FRAME) return null
  const payload = message.payload
  if (typeof payload !== 'object' || payload === null) return null
  const p = payload as Record<string, unknown>
  if (typeof p.agentUserId !== 'string' || p.agentUserId.length === 0) return null
  const focus = p.focusCard
  const focusCardId =
    typeof focus === 'object' &&
    focus !== null &&
    typeof (focus as Record<string, unknown>).id === 'string' &&
    ((focus as Record<string, unknown>).id as string).length > 0
      ? ((focus as Record<string, unknown>).id as string)
      : null
  return { agentUserId: p.agentUserId, focusCardId }
}

/** The agent and card ids of a usable `agent.turn.finished` frame, else `null`. */
export function parseAgentTurnFinishedFrame(
  message: ServerMessage,
): { agentUserId: string; cardId: string | null } | null {
  if (message.type !== AGENT_TURN_FINISHED_FRAME) return null
  const payload = message.payload
  if (typeof payload !== 'object' || payload === null) return null
  const p = payload as Record<string, unknown>
  if (typeof p.agentUserId !== 'string' || p.agentUserId.length === 0) return null
  return {
    agentUserId: p.agentUserId,
    cardId: typeof p.cardId === 'string' && p.cardId.length > 0 ? p.cardId : null,
  }
}

/** Narrow an untyped frame payload; `null` for anything that isn't a usable frame. */
export function parseAgentWorkingFrame(message: ServerMessage): AgentWorkingFramePayload | null {
  if (message.type !== AGENT_WORKING_FRAME) return null
  const payload = message.payload
  if (typeof payload !== 'object' || payload === null) return null
  const p = payload as Record<string, unknown>
  if (typeof p.cardId !== 'string' || p.cardId.length === 0) return null
  if (typeof p.agentUserId !== 'string' || p.agentUserId.length === 0) return null
  if (p.state !== 'start' && p.state !== 'stop') return null
  return {
    cardKind: typeof p.cardKind === 'string' ? p.cardKind : undefined,
    cardId: p.cardId,
    projectId: typeof p.projectId === 'string' ? p.projectId : null,
    agentUserId: p.agentUserId,
    agentName: typeof p.agentName === 'string' ? p.agentName : '',
    state: p.state,
    at: typeof p.at === 'string' ? p.at : undefined,
  }
}

export function useAgentWorkingRealtime(): void {
  const queryClient = useQueryClient()
  const handle = useCallback(
    (message: ServerMessage) => {
      const workstation = parseAgentWorkstationFrame(message)
      if (workstation) {
        noteWorkstationFrame(workstation.agentUserId)
        // A frame without a focus card (the session ended and dropped it)
        // can't say which card's chip to refresh, so refresh every open one.
        void queryClient.invalidateQueries({
          queryKey: workstation.focusCardId
            ? DEEP_LINK_CARD_QUERY_KEY(workstation.focusCardId)
            : DEEP_LINK_CARD_QUERY_PREFIX,
        })
        void queryClient.invalidateQueries({
          queryKey: workstationQueryKey(workstation.agentUserId),
        })
        void queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY })
        return
      }
      const finished = parseAgentTurnFinishedFrame(message)
      if (finished) {
        const cards = new Set(agentWorkingStore.clearAgent(finished.agentUserId))
        if (finished.cardId) cards.add(finished.cardId)
        for (const cardId of cards) {
          void queryClient.invalidateQueries({ queryKey: DEEP_LINK_CARD_QUERY_KEY(cardId) })
        }
        return
      }
      const frame = parseAgentWorkingFrame(message)
      if (!frame) return
      agentWorkingStore.applyFrame(frame)
      if (frame.state === 'stop') {
        void queryClient.invalidateQueries({ queryKey: DEEP_LINK_CARD_QUERY_KEY(frame.cardId) })
      }
    },
    [queryClient],
  )
  useWebSocketSubscription(handle)
}
